import { Injectable } from '@angular/core';
import { AgentRunHistoryEntry } from 'app/modules/agent-library/models/agent-run.model';
import { AgentReply } from 'app/shared/models/agent-reply.model';
import { Observable, delay, of } from 'rxjs';
import { ChatMessage } from '../models/chat.models';
import { buildAgentRepliesFixture } from './agent-replies.fixture';

/**
 * The answer a named agent gives when the widget is talking to it directly.
 *
 * There is no endpoint: the DEO backend has no notion of an agent — `DEOAgentMessageRequest` is
 * `{ session_id, message, conversation_history }` — so a question put to a specific agent is
 * answered here instead of over the wire. `fetchAgentReply` is the single seam that changes when
 * routing does exist, the same shape as `AlertsService.fetchWaitingAlerts` and
 * `AgentsService.fetchAgents`.
 *
 * Questions addressed to DEO itself never reach this service; they go to the real backend exactly
 * as they always have.
 */
@Injectable({
  providedIn: 'root'
})
export class DeoAgentReplyService {
  /**
   * How long the agent appears to think.
   *
   * Not decoration, and not only realism: the widget shows its typing indicator until the reply
   * lands, and a synchronous answer would also write the conversation inside the change-detection
   * pass that asked for it — the `NG0100` that `AlertsService` already had to fix.
   */
  private static readonly THINKING_MS = 400;

  private readonly _replies: Record<string, AgentReply> = buildAgentRepliesFixture();

  /**
   * The agent's answer, or `null` when it has nothing canned to say.
   *
   * A `null` is a real answer to "this agent cannot help with that", not an error — the caller
   * falls back to routing the question to DEO.
   */
  fetchAgentReply(agentId: string): Observable<AgentReply | null> {
    return of(this._replies[agentId] ?? null).pipe(delay(DeoAgentReplyService.THINKING_MS));
  }

  /** Whether an agent has an answer at all, so the caller can decide before it asks. */
  canAnswer(agentId: string): boolean {
    return !!this._replies[agentId];
  }

  /**
   * The conversation a recorded run opens with.
   *
   * Composed from the run rather than written per run, so every agent's history reads the same and a
   * run added to the catalogue gets a conversation without touching this file — the module's own
   * premise, that another agent needs data rather than new code. Part of the same seam: a backend
   * that keeps run transcripts replaces this method, and the widget drops whatever it returns
   * straight into the message list.
   */
  fetchRunConversation(agentId: string, agentName: string, run: AgentRunHistoryEntry): Observable<ChatMessage[]> {
    const asked = new Date();
    const answered = new Date(asked.getTime() + 1000);

    const messages: ChatMessage[] = [
      { content: `What did the ${run.scenario} run find?`, role: 'user', timestamp: asked },
      {
        content: this.describeRun(run),
        role: 'assistant',
        timestamp: answered,
        agentReply: {
          agentId,
          agentName,
          answer: this.describeRun(run),
          followUps: this._replies[agentId]?.followUps ?? []
        }
      }
    ];

    return of(messages).pipe(delay(DeoAgentReplyService.THINKING_MS));
  }

  /** The run's own words, as markdown — the same prose shape a live answer uses. */
  private describeRun(run: AgentRunHistoryEntry): string {
    return [run.outcome, '', '**Run details**', `- Scenario — ${run.scenario}`, `- Recorded — ${run.meta}`].join('\n');
  }
}
